// M4 — Business WEB capture eligibility. The exact counterpart of Android's `CaptureEligibility` and
// iOS's `captureEligibility.ts`.
//
// It re-implements NO policy. The server decides — profile applicability, commercial entitlement,
// role authorization, consent policy and actual consent are all resolved before `capture_readiness`
// is projected onto the appointment. This turns that answer into a control state, and nothing more.
//
// A MISSING entry is not an invitation. An older server that does not send `capture_readiness`, a
// field a proxy strips, or a type this build has never heard of all produce the same answer: not
// enabled. The only path to `enabled` is the server explicitly saying so.
//
// Parity with the two mobile planes is the point: a rule loosened here and left tight there would
// mean Web offers a recording the phones refuse, for the same appointment and the same patient.

/** Types this plane can actually produce. Web captures the provider's call audio; nothing else. */
export const PRODUCIBLE_TYPES = ['audio'];

/** No control at all — the type is not producible, or the server said nothing about it. */
const DENY_SILENT = { offer: false, enabled: false, reason: null };

/**
 * @param {Object|null|undefined} readiness  the server's `capture_readiness` map
 * @param {string} captureType
 * @returns {{offer: boolean, enabled: boolean, reason: string|null}}
 */
export function decide(readiness, captureType) {
  if (!PRODUCIBLE_TYPES.includes(captureType)) return DENY_SILENT;
  const entry = readiness && readiness[captureType];
  if (!entry) return DENY_SILENT;
  if (entry.eligible === true) return { offer: true, enabled: true, reason: null };
  return { offer: true, enabled: false, reason: entry.reason != null ? entry.reason : null };
}

/**
 * May capture actually START right now?
 *
 * Only when it is eligible AND the operator explicitly asked. Both conditions, never either alone:
 * eligibility without an action would record because a booking was opened; an action without
 * eligibility would start a capture the server has already refused.
 */
export function mayStart(decision, operatorRequested) {
  return !!decision && decision.enabled === true && operatorRequested === true;
}

/** A refreshed decision that is no longer enabled must STOP a capture in progress. */
export function mustStop(decision) {
  return !decision || decision.enabled !== true;
}

export default { decide, mayStart, mustStop, PRODUCIBLE_TYPES };
